import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { startGame, endGame, scaleGameToTwoTables } from '../lib/gameService';
import { TopNav } from '../components/TopNav';
import { useLanguage } from '../contexts/LanguageContext';
import {
    ArrowRight, Calendar, Clock, Layers, PlayCircle, StopCircle, Save,
    Zap, Users, ChevronRight, X, Plus, Share2, Check, Activity
} from 'lucide-react';
import { SessionSubNav } from '../components/SessionSubNav';
import clsx from 'clsx';

// ─── Editable Field ───────────────────────────────────────────────
function EditableField({ label, value, onChange, onSave, type = 'text', placeholder = '' }) {
    const { t } = useLanguage();
    const [editing, setEditing] = useState(false);
    const [local, setLocal] = useState(value ?? '');
    const dirty = local !== (value ?? '');

    const handleSave = async () => {
        await onSave(local);
        setEditing(false);
    };

    return (
        <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-b-0">
            <div className="w-28 flex-shrink-0 text-xs font-bold text-slate-500 uppercase tracking-wider font-heebo">{label}</div>
            <div className="flex-1 min-w-0">
                {editing ? (
                    <input
                        type={type}
                        value={local}
                        onChange={e => setLocal(e.target.value)}
                        autoFocus
                        placeholder={placeholder}
                        className="w-full bg-slate-800 border border-amber-500/50 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-500 [color-scheme:dark] font-heebo"
                    />
                ) : (
                    <button
                        onClick={() => setEditing(true)}
                        className="text-sm text-white font-medium hover:text-amber-400 transition-colors text-right w-full font-heebo truncate"
                    >
                        {value || <span className="text-slate-600 italic">{placeholder || '—'}</span>}
                    </button>
                )}
            </div>
            {editing && dirty && (
                <button
                    onClick={handleSave}
                    className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black rounded-lg transition-colors font-heebo"
                >
                    <Save className="w-3.5 h-3.5" />
                    {t('save')}
                </button>
            )}
            {editing && !dirty && (
                <button
                    onClick={() => setEditing(false)}
                    className="flex-shrink-0 text-xs text-slate-500 hover:text-white px-2 font-heebo"
                >✕</button>
            )}
        </div>
    );
}

// ─── Status Badge ─────────────────────────────────────────────────
function StatusBadge({ status }) {
    const { t } = useLanguage();
    const map = {
        ongoing: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
        upcoming: 'text-slate-300 bg-slate-800 border-slate-700',
        completed: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
    };
    const labels = { ongoing: t('ongoing'), upcoming: t('upcoming'), completed: t('historyCompleted') };
    return (
        <span className={clsx('text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg border font-heebo', map[status] || map.upcoming)}>
            {labels[status] || status}
        </span>
    );
}

// ─── Bento Nav Card ───────────────────────────────────────────────
function BentoCard({ onClick, icon, title, subtitle, accent = 'blue', disabled = false }) {
    const accentMap = {
        blue: 'border-blue-500/25 hover:border-blue-400/50 bg-gradient-to-br from-blue-500/8 to-slate-900',
        purple: 'border-purple-500/25 hover:border-purple-400/50 bg-gradient-to-br from-purple-500/8 to-slate-900',
        amber: 'border-amber-500/40 hover:border-amber-400/70 bg-gradient-to-br from-amber-500/12 to-slate-900',
    };
    const iconAccent = {
        blue: 'text-blue-400',
        purple: 'text-purple-400',
        amber: 'text-amber-400',
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={clsx(
                'w-full rounded-2xl border p-4 flex items-center gap-4 text-right transition-all active:scale-[0.98] backdrop-blur-sm shadow-lg',
                accentMap[accent],
                disabled && 'opacity-40 cursor-not-allowed'
            )}
        >
            <div className={clsx('w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0', `bg-${accent === 'amber' ? 'amber' : accent === 'purple' ? 'purple' : 'blue'}-500/15`)}>
                <span className={iconAccent[accent]}>{icon}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-black text-sm text-white font-heebo">{title}</div>
                {subtitle && <div className="text-[10px] text-slate-500 font-medium mt-0.5 truncate">{subtitle}</div>}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0 rtl:rotate-180" />
        </button>
    );
}

// ─── Session Page ─────────────────────────────────────────────────
export const SessionPage = () => {
    const { t, language } = useLanguage();
    const { id } = useParams();
    const navigate = useNavigate();
    const hostId = auth.currentUser?.uid;

    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEndModal, setShowEndModal] = useState(false);
    const [ending, setEnding] = useState(false);
    const [copied, setCopied] = useState(false);

    const appUrl = import.meta.env.VITE_APP_URL || 'https://reshima.io';
    const regLink = `${appUrl}/join/${id}`;

    const copyLink = () => {
        navigator.clipboard.writeText(regLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const [starting, setStarting] = useState(false);
    const [t1Players, setT1Players] = useState(0);
    const [t2Players, setT2Players] = useState(0);

    // Scaling modal state
    const [showScalingModal, setShowScalingModal] = useState(false);
    const [t2Variant, setT2Variant] = useState('No Limit Holdem');
    const [t2Stakes, setT2Stakes] = useState('1/2');
    const [scaling, setScaling] = useState(false);

    useEffect(() => {
        if (!hostId || !id) return;
        const load = async () => {
            try {
                let ref = doc(db, `hosts/${hostId}/games/${id}`);
                let snap = await getDoc(ref);
                if (!snap.exists()) {
                    ref = doc(db, `hosts/${hostId}/sessions/${id}`);
                    snap = await getDoc(ref);
                }
                if (snap.exists()) {
                    setSession({ id: snap.id, ...snap.data() });
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [hostId, id]);

    // Live player counts for multi-table
    useEffect(() => {
        if (!hostId || !session) return;
        const unsubs = [];
        if (session.ledgerId) {
            const ref = doc(db, `hosts/${hostId}/sessions/${session.ledgerId}`);
            unsubs.push(onSnapshot(ref, snap => {
                if (snap.exists()) {
                    const seats = snap.data().tableSeats || {};
                    setT1Players(Object.values(seats).filter(Boolean).length);
                }
            }));
        }
        if (session.ledgerId2) {
            const ref = doc(db, `hosts/${hostId}/sessions/${session.ledgerId2}`);
            unsubs.push(onSnapshot(ref, snap => {
                if (snap.exists()) {
                    const seats = snap.data().tableSeats || {};
                    setT2Players(Object.values(seats).filter(Boolean).length);
                }
            }));
        }
        return () => unsubs.forEach(u => u());
    }, [hostId, session]);

    const patchField = async (field, value) => {
        if (!hostId || !id) return;
        const ref = doc(db, `hosts/${hostId}/games/${id}`);
        await updateDoc(ref, { [field]: value });
        setSession(prev => ({ ...prev, [field]: value }));
    };

    const patchStartTime = async (dateVal, timeVal) => {
        const iso = new Date(`${dateVal}T${timeVal}`).toISOString();
        await patchField('startTime', iso);
    };

    const handleStart = async () => {
        if (!hostId || starting) return;
        setStarting(true);
        try {
            await startGame(hostId, id);
            setSession(prev => ({ ...prev, status: 'ongoing', startedAt: new Date().toISOString() }));
        } catch (err) { console.error(err); }
        finally { setStarting(false); }
    };

    const handleEnd = async () => {
        if (!hostId || ending) return;
        setEnding(true);
        try {
            await endGame(hostId, id, {});
            setSession(prev => ({ ...prev, status: 'completed', endedAt: new Date().toISOString() }));
            setShowEndModal(false);
        } catch (err) { console.error(err); }
        finally { setEnding(false); }
    };

    const handleScale = async () => {
        if (!hostId || scaling) return;
        setScaling(true);
        try {
            await scaleGameToTwoTables(hostId, id, {
                variant: t2Variant,
                stakes: t2Stakes,
                minBuyIn: t2Stakes.split('/')[0] * 50 // Default logic
            });
            setSession(prev => ({ ...prev, capacity: '2 Tables', table2Config: { variant: t2Variant, stakes: t2Stakes } }));
            setShowScalingModal(false);
        } catch (err) { console.error(err); }
        finally { setScaling(false); }
    };

    // ─── Derived values ───────────────────────────────────────────
    const startDate = session?.startTime ? new Date(session.startTime).toLocaleDateString('en-CA') : '';
    const startTime = session?.startTime
        ? new Date(session.startTime).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })
        : '';
    const displayDate = session?.startTime
        ? new Date(session.startTime).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })
        : '';
    const displayTime = session?.startTime
        ? new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
        : '';

    const isMultiTable = session?.capacity === '2 Tables';

    const t1Label = isMultiTable
        ? `${session.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} · ${session.table1Config?.stakes || '?'}`
        : (session?.variant?.includes('PLO') ? 'PLO' : 'NLH') + ' · ' + (session?.stakes || '?');
    const t2Label = `${session?.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} · ${session?.table2Config?.stakes || '?'}`;

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col">
                <TopNav />
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-7 h-7 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col">
                <TopNav />
                <div className="flex-1 flex items-center justify-center text-slate-500 font-heebo">{t('sessionNotFound')}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />
            <SessionSubNav sessionId={id} isMultiTable={isMultiTable} />

            {/* ── Header Row ── */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-slate-900/60 gap-2">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm font-heebo flex-shrink-0"
                >
                    <ArrowRight className="w-4 h-4" />
                    {t('back')}
                </button>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={session.status} />
                    {session.status === 'upcoming' && (
                        <button
                            onClick={handleStart}
                            disabled={starting}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 text-xs font-black rounded-lg transition-colors font-heebo"
                        >
                            <PlayCircle className="w-3.5 h-3.5" />
                            {starting ? '...' : t('startNow')}
                        </button>
                    )}
                    {session.status === 'ongoing' && (
                        <button
                            onClick={() => setShowEndModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/30 text-rose-400 text-xs font-black rounded-lg transition-colors font-heebo"
                        >
                            <StopCircle className="w-3.5 h-3.5" />
                            {t('endSession')}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Main content — scrollable ── */}
            <div className="flex-1 overflow-y-auto">
                <div className="w-full max-w-xl mx-auto px-4 py-6 space-y-5">

                    {/* ── Info Card (Centerpiece) ── */}
                    <div className="relative rounded-3xl overflow-hidden border border-white/8 bg-gradient-to-br from-slate-800/80 to-slate-900/90 backdrop-blur-xl shadow-2xl">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent pointer-events-none" />
                        
                        {/* Top accent line */}
                        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />

                        <div className="p-5 space-y-4">
                            {/* Date & Time */}
                            <div className="flex flex-col items-center justify-center pt-2 pb-6 border-b border-white/5 relative">
                                {isMultiTable && (
                                    <div className="absolute top-0 right-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30">
                                        <Layers className="w-3 h-3 text-amber-400" />
                                        <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider">{t('twoTables')}</span>
                                    </div>
                                )}
                                <div className="text-5xl font-black text-white font-heebo leading-tight tracking-tighter drop-shadow-lg text-center">{displayDate}</div>
                                <div className="flex items-center gap-3 mt-4 bg-slate-950/40 px-6 py-2 rounded-2xl border border-white/5 shadow-inner">
                                    <Clock className="w-6 h-6 text-amber-500" />
                                    <span className="text-3xl text-white font-heebo tabular-nums font-black tracking-[0.15em]">{displayTime}</span>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                {/* Game Configs (Visual Right, DOM First) */}
                                <div className="flex-[2] flex flex-col justify-center min-w-0">
                                    {isMultiTable ? (
                                        <div className="grid grid-cols-2 gap-2 h-full">
                                            <div className="bg-blue-500/8 border border-blue-500/20 rounded-xl px-2 py-3 flex flex-col justify-center items-center text-center">
                                                <div className="text-[9px] text-blue-400/70 uppercase font-black tracking-wider mb-1">{t('table1')}</div>
                                                <div className="text-xs font-bold text-white truncate w-full">{t1Label}</div>
                                            </div>
                                            <div className="bg-purple-500/8 border border-purple-500/20 rounded-xl px-2 py-3 flex flex-col justify-center items-center text-center">
                                                <div className="text-[9px] text-purple-400/70 uppercase font-black tracking-wider mb-1">{t('table2')}</div>
                                                <div className="text-xs font-bold text-white truncate w-full">{t2Label}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-full flex flex-col justify-center items-start bg-slate-900/40 border border-white/5 rounded-2xl p-4">
                                            <span className="text-lg font-black text-amber-400 font-heebo truncate w-full text-right">{t1Label}</span>
                                            {session.stakes && (
                                                <div className="text-sm text-slate-400 font-heebo mt-1.5 bg-slate-950/50 px-3 py-1 rounded-lg border border-white/5">{t('min')}: ₪{session.minBuyIn || '—'}</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Registration HUD (Visual Left, DOM Last) */}
                                <div className="flex-[1.2] flex flex-col items-center justify-center bg-cyan-500/20 border border-cyan-400/50 rounded-2xl p-6 shadow-[0_0_40px_rgba(34,211,238,0.15)] relative overflow-hidden ring-2 ring-cyan-500/20 scale-105 transform">
                                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 via-transparent to-transparent pointer-events-none animate-pulse" />
                                    <div className="absolute inset-0 rounded-2xl ring-2 ring-cyan-300/30 animate-pulse pointer-events-none" />
                                    <Users className="w-6 h-6 text-cyan-400 mb-3 relative z-10" />
                                    <div className="text-5xl font-black text-white tabular-nums tracking-tighter leading-none relative z-10">{t1Players + t2Players}</div>
                                    <div className="text-[11px] font-black text-cyan-200 uppercase tracking-[0.2em] mt-3 bg-cyan-500 border border-cyan-400 px-4 py-1 rounded-full relative z-10 shadow-lg">{t('registered')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* ── Registration Link Card ── */}
                    <div className="bg-slate-900/70 border border-white/5 rounded-2xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Share2 className="w-4 h-4 text-amber-500" />
                                <h2 className="text-sm font-bold text-slate-300 font-heebo">{t('registrationLink')}</h2>
                            </div>
                            <button
                                onClick={copyLink}
                                className={clsx(
                                    "flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all",
                                    copied ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-amber-500 text-slate-950 hover:bg-amber-400"
                                )}
                            >
                                {copied ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                                {copied ? t('copied') : t('copy')}
                            </button>
                        </div>
                        <div className="p-4">
                            <div
                                onClick={copyLink}
                                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-xs font-mono text-slate-400 truncate cursor-pointer hover:border-slate-700 transition-colors"
                            >
                                {regLink}
                            </div>
                            <p className="text-[10px] text-slate-600 mt-2 text-center uppercase font-black tracking-widest">{t('shareWithPlayers')}</p>
                        </div>
                    </div>

                    {/* ── Editable Fields Card ── */}
                    <div className="bg-slate-900/70 border border-white/5 rounded-2xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                            <Layers className="w-4 h-4 text-amber-500" />
                            <h2 className="text-sm font-bold text-slate-300 font-heebo">{t('sessionDetails')}</h2>
                        </div>
                        <div className="px-4">
                            {!isMultiTable && (
                                <EditableField
                                    label={t('stakes')}
                                    value={session.stakes}
                                    onSave={v => patchField('stakes', v)}
                                    placeholder="1/2"
                                />
                            )}
                            <EditableField
                                label={t('minBuyIn')}
                                value={session.minBuyIn ? String(session.minBuyIn) : ''}
                                type="number"
                                onSave={v => patchField('minBuyIn', parseFloat(v) || 0)}
                                placeholder="200"
                            />
                            <EditableField
                                label={t('sessionDate')}
                                value={startDate}
                                type="date"
                                onSave={v => patchStartTime(v, startTime)}
                                placeholder="YYYY-MM-DD"
                            />
                            <EditableField
                                label={t('sessionTime')}
                                value={startTime}
                                type="time"
                                onSave={v => patchStartTime(startDate, v)}
                                placeholder="HH:MM"
                            />
                        </div>
                    </div>

                    {/* ── Navigation Hub ── */}
                    <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-black mb-4 font-heebo text-center">{t('dashboardCommandCenter')}</p>
                        {isMultiTable ? (
                            <div className="grid grid-cols-1 gap-3">
                                <BentoCard
                                    onClick={() => navigate(`/session/${id}/ledger/1`)}
                                    icon={<Activity className="w-5 h-5" />}
                                    title={t('table1Ledger')}
                                    subtitle={t1Label}
                                    accent="blue"
                                />
                                <BentoCard
                                    onClick={() => navigate(`/session/${id}/ledger/2`)}
                                    icon={<Activity className="w-5 h-5" />}
                                    title={t('table2Ledger')}
                                    subtitle={t2Label}
                                    accent="purple"
                                />
                                <BentoCard
                                    onClick={() => navigate(`/session/${id}/ledger/master`)}
                                    icon={<Layers className="w-5 h-5" />}
                                    title={t('masterFinancialLedger')}
                                    subtitle={t('consolidatedTotals')}
                                    accent="amber"
                                />
                            </div>
                        ) : (
                            <BentoCard
                                onClick={() => navigate(`/session/${id}/ledger/1`)}
                                icon={<Activity className="w-5 h-5" />}
                                title={t('openLedger')}
                                subtitle={t1Label}
                                accent="amber"
                            />
                        )}

                        {/* Scale to 2 Tables Card */}
                        {!isMultiTable && (
                            <button
                                onClick={() => setShowScalingModal(true)}
                                className="group relative flex items-center justify-between p-6 bg-slate-900/40 border border-dashed border-amber-500/20 hover:border-amber-500/40 rounded-3xl transition-all w-full text-right overflow-hidden shadow-sm hover:shadow-amber-500/5 active:scale-[0.99]"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-transparent blur-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 group-hover:scale-110 transition-transform">
                                        <Layers className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="text-lg font-black text-white font-heebo mb-0.5 tracking-tight group-hover:text-amber-400 transition-colors">{t('addSecondTable')}</div>
                                        <div className="text-xs text-slate-500 font-heebo">{t('addSecondTableDesc')}</div>
                                    </div>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-500 group-hover:bg-amber-500 group-hover:text-amber-950 transition-all">
                                    <Plus className="w-4 h-4" />
                                </div>
                            </button>
                        )}
                    </div>

                    {/* Bottom padding for scroll */}
                    <div className="h-8" />
                </div>
            </div>

            {/* ── Scaling Modal ── */}
            {showScalingModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 w-full max-w-sm space-y-6 animate-in fade-in zoom-in duration-200">
                        <div>
                            <h2 className="text-xl font-black text-white font-heebo mb-2">{t('table2Setup')}</h2>
                            <p className="text-sm text-slate-400">{t('table2SetupDesc')}</p>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 mr-1">{t('gameType')}</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['No Limit Holdem', 'Omaha PLO'].map(v => (
                                        <button 
                                            key={v}
                                            onClick={() => setT2Variant(v)}
                                            className={clsx(
                                                "py-3 rounded-xl border font-bold text-xs transition-all",
                                                t2Variant === v ? "bg-amber-500 border-amber-400 text-slate-950" : "bg-slate-800 border-white/5 text-slate-400 hover:border-white/10"
                                            )}
                                        >
                                            {v === 'Omaha PLO' ? 'PLO' : 'NLH'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 mr-1">{t('stakes')}</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {['1/2', '2/5', '5/5', '5/10', '10/25', '25/50'].map(s => (
                                        <button 
                                            key={s}
                                            onClick={() => setT2Stakes(s)}
                                            className={clsx(
                                                "py-2 rounded-xl border font-bold text-xs transition-all",
                                                t2Stakes === s ? "bg-amber-400 border-amber-300 text-slate-950" : "bg-slate-800/50 border-white/5 text-slate-400"
                                            )}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button 
                                onClick={handleScale}
                                disabled={scaling}
                                className="flex-1 py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-2xl transition-all shadow-[0_10px_30px_rgba(245,158,11,0.2)]"
                            >
                                {scaling ? t('scaling') : t('confirmAndStart')}
                            </button>
                            <button 
                                onClick={() => setShowScalingModal(false)}
                                className="px-6 py-4 bg-slate-800 text-slate-400 font-bold rounded-2xl hover:bg-slate-700 transition-colors"
                            >
                                {t('cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* ── End Session Confirmation Modal ── */}
            {showEndModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-rose-500/40 rounded-2xl overflow-hidden shadow-2xl">
                        <div className="p-5 border-b border-rose-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <StopCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
                                <h3 className="text-base font-bold text-white font-heebo">{t('endSession')}</h3>
                            </div>
                            <button onClick={() => setShowEndModal(false)} className="text-slate-600 hover:text-white transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {/* Summary for multi-table */}
                        {isMultiTable && (
                            <div className="px-5 pt-4 pb-0">
                                <div className="grid grid-cols-2 gap-2 text-center">
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl py-3">
                                        <div className="text-[9px] text-blue-400 uppercase font-black tracking-wider">{t('table')} 1 {t('players')}</div>
                                        <div className="text-xl font-black text-white mt-0.5">{t1Players}</div>
                                    </div>
                                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl py-3">
                                        <div className="text-[9px] text-purple-400 uppercase font-black tracking-wider">{t('table')} 2 {t('players')}</div>
                                        <div className="text-xl font-black text-white mt-0.5">{t2Players}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="p-5 space-y-5">
                            <p className="text-slate-300 text-sm font-heebo leading-relaxed">{t('endSessionConfirm')}</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setShowEndModal(false)}
                                    className="py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold border border-slate-700 transition-colors font-heebo"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={handleEnd}
                                    disabled={ending}
                                    className="py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold transition-colors disabled:opacity-50 font-heebo"
                                >
                                    {ending ? '...' : t('confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
